import { useEffect, useRef, useState } from 'react';
import { getJobStatus, retryJob, type JobState, type JobStatus } from '../../api/jobs';
import Button from '../common/Button';

// ─── Props ───────────────────────────────────────────────────────────────────

interface ExportProgressModalProps {
  jobId: string;
  title: string;
  isOpen: boolean;
  onClose: () => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function stateLabel(state: JobState, progress: number): string {
  switch (state) {
    case 'created':
      return 'Queued — waiting to start…';
    case 'running':
      return `Generating (${progress}%)`;
    case 'completed':
      return 'Complete';
    case 'failed':
      return 'Failed';
    case 'failed_permanent':
      return 'Failed permanently';
    default:
      return 'Unknown';
  }
}

function stateColor(state: JobState): string {
  switch (state) {
    case 'completed':
      return 'bg-green-500';
    case 'failed':
    case 'failed_permanent':
      return 'bg-red-500';
    case 'running':
      return 'bg-blue-500 animate-pulse';
    default:
      return 'bg-amber-500';
  }
}

function formatDuration(startIso: string): string {
  const seconds = Math.floor((Date.now() - new Date(startIso).getTime()) / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${minutes}m ${secs}s`;
}

const STEPS = ['Job queued', 'Fetching data', 'Processing records', 'Generating file', 'Upload complete'];

function getCompletedSteps(state: JobState, progress: number): number {
  if (state === 'completed') return STEPS.length;
  if (state === 'created') return 0;
  // running: map 0-100 progress to step index
  // All steps can be active during running state; completed state sets all to done
  return Math.min(Math.floor((progress / 100) * STEPS.length), STEPS.length - 1);
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function ExportProgressModal({ jobId, title, isOpen, onClose }: ExportProgressModalProps) {
  const [job, setJob] = useState<JobStatus | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      const el = dialogRef.current?.querySelector<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      el?.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const stopPolling = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const poll = async () => {
    try {
      const data = await getJobStatus(jobId);
      setJob(data);
      setLoadError(null);
      if (data.state === 'completed' || data.state === 'failed' || data.state === 'failed_permanent') {
        stopPolling();
      }
    } catch {
      setLoadError('Could not load job status.');
    }
  };

  useEffect(() => {
    if (!isOpen || !jobId) return;

    void poll();
    intervalRef.current = setInterval(() => { void poll(); }, 2000);

    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, jobId]);

  const handleRetry = async () => {
    if (!job) return;
    setRetrying(true);
    setRetryError(null);
    try {
      await retryJob(job.id);
      // Delay slightly before restarting polling so the database state is consistent
      setTimeout(() => {
        intervalRef.current = setInterval(() => { void poll(); }, 2000);
      }, 1000);
    } catch {
      setRetryError('Failed to retry job. Please try again.');
    } finally {
      setRetrying(false);
    }
  };

  if (!isOpen) return null;

  const state = job?.state ?? 'created';
  const progress = job?.progress ?? 0;
  const isFinal = state === 'completed' || state === 'failed' || state === 'failed_permanent';
  const isError = state === 'failed' || state === 'failed_permanent';
  const downloadUrl = job?.result?.downloadUrl as string | undefined;
  const completedSteps = getCompletedSteps(state, progress);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="export-progress-modal-title"
        className="relative w-full max-w-md mx-4 rounded-xl border border-border bg-surface shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 id="export-progress-modal-title" className="text-base font-semibold text-text-primary">{title}</h2>
          {isFinal && (
            <button
              onClick={onClose}
              className="text-text-muted hover:text-text-primary transition-colors rounded-lg p-1 hover:bg-surface-hover"
              aria-label="Close"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          {/* Status label */}
          {loadError ? (
            <p className="text-sm text-red-400">{loadError}</p>
          ) : (
            <>
              <div className="flex items-center gap-3">
                <span className={`h-2.5 w-2.5 rounded-full flex-shrink-0 ${stateColor(state)}`} />
                <span className="text-sm font-medium text-text-primary">{stateLabel(state, progress)}</span>
                {job?.createdAt && !isFinal && (
                  <span className="ml-auto text-xs text-text-muted">{formatDuration(job.createdAt)} elapsed</span>
                )}
              </div>

              {/* Progress bar */}
              <div className="w-full h-2 bg-surface-active rounded-full overflow-hidden">
                <div
                  className={`h-2 rounded-full transition-all duration-500 ${isError ? 'bg-red-500' : 'bg-primary-500'}`}
                  style={{ width: `${state === 'completed' ? 100 : progress}%` }}
                />
              </div>

              {/* Steps */}
              <ol className="space-y-2">
                {STEPS.map((step, idx) => {
                  const done = idx < completedSteps;
                  const active = idx === completedSteps && !isFinal;
                  const failed = isError && idx === completedSteps;
                  return (
                    <li key={step} className="flex items-center gap-2.5 text-sm">
                      {done ? (
                        <svg className="h-4 w-4 text-green-400 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      ) : failed ? (
                        <svg className="h-4 w-4 text-red-400 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      ) : active ? (
                        <svg className="h-4 w-4 text-primary-400 animate-spin flex-shrink-0" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                      ) : (
                        <span className="h-4 w-4 rounded-full border border-border flex-shrink-0 bg-surface-active" />
                      )}
                      <span className={done ? 'text-text-primary' : active ? 'text-primary-400' : 'text-text-muted'}>
                        {step}
                      </span>
                    </li>
                  );
                })}
              </ol>

              {/* Error message */}
              {isError && job?.error && (
                <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
                  <span className="font-medium">Error: </span>{job.error}
                </div>
              )}

              {/* Retry error */}
              {retryError && (
                <p className="text-xs text-red-400">{retryError}</p>
              )}
            </>
          )}
        </div>

        {/* Footer actions */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-border">
          {state === 'completed' && downloadUrl ? (
            <a
              href={downloadUrl}
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 hover:bg-primary-700 px-4 py-2 text-sm font-medium text-white transition-colors"
              download
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download
            </a>
          ) : null}

          {state === 'completed' && !downloadUrl ? (
            <Button size="sm" onClick={onClose}>Done</Button>
          ) : null}

          {isError && (
            <Button
              size="sm"
              variant="outline"
              isLoading={retrying}
              onClick={() => { void handleRetry(); }}
            >
              Retry
            </Button>
          )}

          {isFinal && (
            <Button size="sm" variant="ghost" onClick={onClose}>
              Close
            </Button>
          )}

          {!isFinal && (
            <span className="text-xs text-text-muted">Processing… please wait</span>
          )}
        </div>
      </div>
    </div>
  );
}
